import qs from "query-string";
import { useEffect, useState, useCallback } from "react";

import useParentLoaderData from "../../../../../hooks/useParentLoaderData";
import useService from "../../../../../hooks/useService";
import { getModalHandler } from "../../../Common/Modals/hooks/useModalSingleton";
import { useAvailableServicesStore } from "../store/services-provided/availableServicesStore";
import { useProvidedServicesStore } from "../store/services-provided/providedServicesStore";
import STRINGS from "../utils/strings";

export default function useFetchTabData() {
  const providerOfficeHoursRepository = useService("repositories/provider-office-hours-repository");
  const enumRepository = useService("repositories/enum-repository");
  const availableServicesRepository = useService("repositories/service-repository");
  const providedServicesRepository = useService("repositories/service-repository");
  const { id } = useParentLoaderData(2);
  const { openFailureModal } = getModalHandler();
  const availableServicesStore = useAvailableServicesStore();
  const providedServicesStore = useProvidedServicesStore();

  const [days, setDays] = useState([]);
  const [timings, setTimings] = useState([]);
  const [loadingHoursOfOperation, setLoadingHoursOfOperation] = useState(false);

  const calculateOffset = (store) => {
    const { currentPage, resLimit } = store;
    return (Number(currentPage) - 1) * resLimit || 0;
  };

  const fetchDays = useCallback(async () => {
    try {
      const queryParams = {
        content_type: "providerofficehours",
        field_name: "day",
        flat: "True",
      };

      const url = `field_options/?${qs.stringify(queryParams)}`;
      const response = await enumRepository.query({ urlPath: url });

      const daysArray = Array.from({ length: 7 }, () => null);

      response.forEach((dataPoint) => {
        daysArray[Number(dataPoint.order - 1)] = dataPoint.label;
      });

      setDays(daysArray);
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    }
  }, [enumRepository]);

  const fetchTimings = useCallback(async () => {
    try {
      setLoadingHoursOfOperation(true);
      const queryParams = {
        ordering: "day,start_time",
        ...(id && { provider: id }),
      };
      const response = await providerOfficeHoursRepository.query(queryParams);

      setTimings(response);
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    } finally {
      setLoadingHoursOfOperation(false);
    }
  }, [id, providerOfficeHoursRepository]);

  const fetchAvailableServices = useCallback(async () => {
    try {
      availableServicesStore.setLoading(true);
      const { resLimit, currentPage, currentSearch, setData, setResultCount } =
        availableServicesStore;

      const queryParams = {};
      if (id) {
        queryParams.providerId = id;
      }
      if (currentSearch.trim()) {
        queryParams.search = currentSearch.trim();
      }
      if (resLimit && currentPage) {
        queryParams.limit = resLimit;
        queryParams.offset = calculateOffset(availableServicesStore);
      }

      const responseData = await availableServicesRepository.query(queryParams);
      const count = await availableServicesRepository.getTotalCount();

      const updatedData = responseData.map((service) => ({
        ...service,
        serviceType: STRINGS.SERVICE_TYPE.AVAILABLE,
      }));

      setData(updatedData ?? []);
      setResultCount(count);
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    } finally {
      availableServicesStore.setLoading(false);
    }
  }, [
    id,
    availableServicesRepository,
    availableServicesStore.currentPage,
    availableServicesStore.currentSearch,
  ]);

  const fetchProvidedServices = useCallback(async () => {
    try {
      providedServicesStore.setLoading(true);
      const { resLimit, currentPage, currentSearch, setData, setResultCount } =
        providedServicesStore;

      const queryParams = {};
      if (id) {
        queryParams.providerId = id;
      }
      if (currentSearch.trim()) {
        queryParams.search = currentSearch.trim();
      }
      if (resLimit && currentPage) {
        queryParams.limit = providedServicesStore.resLimit;
        queryParams.offset = calculateOffset(providedServicesStore);
      }

      const responseData = await availableServicesRepository.query(queryParams, false);
      const count = await availableServicesRepository.getTotalCount();
      const updatedData = responseData.map((item) => ({
        ...item.service,
        serviceType: STRINGS.SERVICE_TYPE.PROVIDED,
      }));

      setData(updatedData ?? []);
      setResultCount(count);
    } catch (error) {
      console.error(error);
      openFailureModal(error);
    } finally {
      providedServicesStore.setLoading(false);
    }
  }, [
    id,
    providedServicesRepository,
    providedServicesStore.currentPage,
    providedServicesStore.currentSearch,
  ]);

  useEffect(() => {
    fetchDays();
  }, [fetchDays]);

  useEffect(() => {
    fetchTimings();
  }, [fetchTimings]);

  useEffect(() => {
    fetchAvailableServices();
  }, [
    fetchAvailableServices,
    availableServicesStore.currentPage,
    availableServicesStore.currentSearch,
  ]);

  useEffect(() => {
    fetchProvidedServices();
  }, [
    fetchProvidedServices,
    providedServicesStore.currentPage,
    providedServicesStore.currentSearch,
  ]);

  const mergedDays = days.map((dayLabel, index) => {
    const timingsForDay = timings.filter((t) => Number(t.day) === index);

    return {
      day: dayLabel,
      timings: timingsForDay,
    };
  });

  return {
    days: mergedDays,
    loadingHoursOfOperation,
    refreshTimings: fetchTimings,
    refreshAvailableServices: fetchAvailableServices,
    refreshProvidedServices: fetchProvidedServices,
  };
}
